import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiTrash2, FiSearch, FiEdit2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { today, formatDate, unwrapList } from '../../utils/format';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyRow = () => ({ date: today(), machine_id: '', employee_id: '', stitch_count: '', apply_threshold: true, special_thread: false, remarks: '' });

const dayAmount = (machine, count, applyThreshold = true) => {
  if (!machine || count === '' || count === null || count === undefined) return null;
  const c = Number(count);
  const threshold = Number(machine.threshold_stitches);
  if (c >= threshold || applyThreshold === false) return (c / 100000) * Number(machine.rate_per_1000);
  return Number(machine.fixed_below_threshold);
};

export default function Stitches() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', start_date: '', end_date: '', employee_id: '', per_page: 100 });
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [machines, setMachines] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bulk, setBulk] = useState(false);
  const [records, setRecords] = useState([emptyRow()]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.stitches.list(params);
      const page = data.stitches || data;
      setRows(Array.isArray(page.data) ? page.data : Array.isArray(page) ? page : []);
      setMeta(page.data ? { current_page: page.current_page, last_page: page.last_page, total: page.total, per_page: page.per_page, per_page_options: data.per_page_options || page.per_page_options } : null);
      setTotals(data.totals || null);
    } catch (err) {
      showError('Failed to load stitches', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.machines.list().then(({ data }) => {
      setMachines(unwrapList(data, 'machines'));
    }).catch(() => {});
    api.employees.list({ employee_type: 'operator', per_page: 500 }).then(({ data }) => {
      setEmployees(unwrapList(data, 'employees'));
    }).catch(() => {});
  }, []);

  const updateRecord = (i, field, value) => {
    const next = [...records];
    next[i] = { ...next[i], [field]: value };
    setRecords(next);
  };

  const handleBulkSave = async () => {
    if (saving) return;
    const errs = {};
    if (!records.length) errs.records = 'At least one record is required';
    if (records.some((r) => !r.date || !r.machine_id || !r.employee_id || !r.stitch_count)) errs.records = 'All rows are required - fill machine, employee and stitch count for every row';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.stitches.bulk({
        records: records.map((r) => ({
          date: r.date,
          machine_id: Number(r.machine_id),
          employee_id: Number(r.employee_id),
          stitch_count: Number(r.stitch_count),
          apply_threshold: r.apply_threshold !== false,
          special_thread: r.special_thread,
          remarks: r.remarks || undefined,
        })),
      });
      toast('success', '', data.message || `${records.length} stitch record(s) saved`);
      setBulk(false);
      setRecords([emptyRow()]);
      load();
    } catch (err) {
      showError('Failed to save stitches', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setErrors({});
    setEdit({
      id: row.id,
      date: row.date ? String(row.date).slice(0, 10) : today(),
      machine_id: row.machine_id || '',
      employee_id: row.employee_id || '',
      stitch_count: row.stitch_count ?? '',
      apply_threshold: row.apply_threshold !== false,
      special_thread: !!row.special_thread,
      remarks: row.remarks || '',
    });
  };

  const handleEditSave = async () => {
    if (saving || !edit) return;
    const errs = {};
    if (!edit.date) errs.edit = 'Date is required';
    if (!edit.machine_id) errs.edit = 'Machine is required';
    if (!edit.employee_id) errs.edit = 'Operator is required';
    if (!edit.stitch_count) errs.edit = 'Stitch count is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.stitches.update(edit.id, {
        date: edit.date,
        machine_id: Number(edit.machine_id),
        employee_id: Number(edit.employee_id),
        stitch_count: Number(edit.stitch_count),
        apply_threshold: edit.apply_threshold !== false,
        special_thread: edit.special_thread,
        remarks: edit.remarks || undefined,
      });
      toast('success', '', data.message || 'Stitch record updated');
      setEdit(null);
      setErrors({});
      load();
    } catch (err) {
      showError('Failed to update record', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete('this stitch record');
    if (!res.isConfirmed) return;
    try {
      await api.stitches.remove(row.id);
      toast('success', '', 'Stitch record deleted');
      load();
    } catch (err) {
      showError('Failed to delete record', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Stitches"
        subtitle="Daily production entries for operators."
        crumbs={[{ label: 'Production' }, { label: 'Stitches' }]}
        actions={hasPermission('create-stitches') && (
          <button className="btn btn-primary" onClick={() => { setErrors({}); setBulk(true); }}><FiPlus /> Add Stitches</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search employee/machine..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
            <SearchableSelect
              options={employees}
              value={params.employee_id}
              onChange={(v) => setParams({ ...params, employee_id: v, page: 1 })}
              placeholder="-- All Operators --"
            />
            <input className="form-control" type="date" title="Start date" value={params.start_date} onChange={(e) => setParams({ ...params, start_date: e.target.value, page: 1 })} />
            <input className="form-control" type="date" title="End date" value={params.end_date} onChange={(e) => setParams({ ...params, end_date: e.target.value, page: 1 })} />
            {(params.start_date || params.end_date || params.employee_id || params.search) && (
              <button className="btn btn-secondary btn-sm" onClick={() => setParams({ page: 1, search: '', start_date: '', end_date: '', employee_id: '', per_page: params.per_page })}>Clear</button>
            )}
          </div>

          {totals && (
            <div className="totals-bar" style={{ display: 'flex', gap: 24, padding: '10px 14px', marginBottom: 12, background: 'var(--bg-soft, #f5f7fb)', borderRadius: 8, fontWeight: 600 }}>
              <span>Total Stitches: <strong>{Number(totals.stitch_count).toLocaleString()}</strong></span>
              <span>Total Salary: <strong>PKR {Number(totals.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
            </div>
          )}

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="stitches" label="stitches" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No stitch records found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
              { key: 'machine', label: 'Machine', render: (r) => (r.machine ? `${r.machine.name} (${r.machine.head_type}h)` : '-') },
              { key: 'employee', label: 'Employee', render: (r) => (r.employee ? r.employee.name : '-') },
              { key: 'stitch_count', label: 'Stitches', align: 'right', render: (r) => Number(r.stitch_count).toLocaleString() },
              { key: 'amount', label: 'Amount (PKR)', align: 'right', render: (r) => {
                const amt = dayAmount(r.machine, r.stitch_count, r.apply_threshold !== false);
                return amt === null ? '-' : amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              } },
              { key: 'special_thread', label: 'Special Thread', render: (r) => r.special_thread ? <span className="badge badge-purple">Yes</span> : <span className="badge badge-gray">No</span> },
              { key: 'remarks', label: 'Remarks' },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('create-stitches') && <button className="btn-icon" title="Edit" onClick={() => openEdit(r)}><FiEdit2 /></button>}
                  {hasPermission('delete-stitches') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal open={bulk} onClose={() => { if (!saving) { setErrors({}); setBulk(false); } }} title="Add Stitch Records" size="xl"
        footer={<>
          <button className="btn btn-secondary" onClick={() => { if (!saving) { setErrors({}); setBulk(false); } }}>Cancel</button>
          <button className="btn btn-primary" onClick={handleBulkSave} disabled={saving}>{saving ? 'Saving...' : 'Save Records'}</button>
        </>}
      >
        {errors.records && <div className="invalid-feedback" style={{ marginBottom: 10 }}>{errors.records}</div>}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Date</th><th>Machine</th><th>Operator</th>
                <th style={{ width: 130 }}>Stitches</th><th style={{ width: 130 }}>Amount</th><th style={{ width: 130 }}>Special</th><th>Remarks</th><th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i}>
                  <td><input className="form-control" type="date" value={r.date} onChange={(e) => updateRecord(i, 'date', e.target.value)} /></td>
                  <td>
                    <SearchableSelect options={machines} value={r.machine_id} onChange={(v) => updateRecord(i, 'machine_id', v)} getLabel={(m) => `${m.name} (${m.head_type}h)`} placeholder="-- Machine --" />
                  </td>
                  <td>
                    <SearchableSelect options={employees} value={r.employee_id} onChange={(v) => updateRecord(i, 'employee_id', v)} placeholder="-- Operator --" />
                  </td>
                  <td>
                    <input className="form-control" type="number" value={r.stitch_count} onChange={(e) => updateRecord(i, 'stitch_count', e.target.value)} />
                    {(() => {
                      const m = machines.find((x) => String(x.id) === String(r.machine_id));
                      const c = Number(r.stitch_count);
                      if (!m || !c || c >= Number(m.threshold_stitches)) return null;
                      return (
                        <label
                          title="Stitches below threshold: checked = fixed amount (1400), unchecked = normal rate calculation"
                          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 5, color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}
                        >
                          <input
                            type="checkbox"
                            checked={r.apply_threshold !== false}
                            onChange={(e) => updateRecord(i, 'apply_threshold', e.target.checked)}
                            style={{ width: 14, height: 14 }}
                          />
                          Apply threshold
                        </label>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {(() => {
                      const m = machines.find((x) => String(x.id) === String(r.machine_id));
                      const amt = dayAmount(m, r.stitch_count, r.apply_threshold !== false);
                      return amt === null ? '-' : amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    })()}
                  </td>
                  <td><input type="checkbox" checked={r.special_thread} onChange={(e) => updateRecord(i, 'special_thread', e.target.checked)} style={{ width: 18, height: 18 }} /></td>
                  <td><input className="form-control" value={r.remarks} onChange={(e) => updateRecord(i, 'remarks', e.target.value)} /></td>
                  <td>
                    <button className="btn-icon danger" onClick={() => setRecords(records.filter((_, idx) => idx !== i))}><FiTrash2 /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setRecords([...records, emptyRow()])}><FiPlus /> Add Row</button>
      </Modal>

      <Modal
        open={!!edit}
        onClose={() => { if (!saving) { setEdit(null); setErrors({}); } }}
        title="Edit Stitch Record"
        footer={<>
          <button className="btn btn-secondary" onClick={() => { if (!saving) { setEdit(null); setErrors({}); } }}>Cancel</button>
          <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving...' : 'Update Record'}</button>
        </>}
      >
        {edit && (
          <>
            {errors.edit && <div className="invalid-feedback" style={{ marginBottom: 10 }}>{errors.edit}</div>}
            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">Date <span className="req">*</span></label>
                <input className="form-control" type="date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Machine <span className="req">*</span></label>
                <SearchableSelect options={machines} value={edit.machine_id} onChange={(v) => setEdit({ ...edit, machine_id: v })} getLabel={(m) => `${m.name} (${m.head_type}h)`} placeholder="-- Machine --" />
              </div>
              <div className="form-group">
                <label className="form-label">Operator <span className="req">*</span></label>
                <SearchableSelect options={employees} value={edit.employee_id} onChange={(v) => setEdit({ ...edit, employee_id: v })} placeholder="-- Operator --" />
              </div>
            </div>
            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">Stitches <span className="req">*</span></label>
                <input className="form-control" type="number" value={edit.stitch_count} onChange={(e) => setEdit({ ...edit, stitch_count: e.target.value })} />
                {(() => {
                  const m = machines.find((x) => String(x.id) === String(edit.machine_id));
                  const c = Number(edit.stitch_count);
                  if (!m || !c || c >= Number(m.threshold_stitches)) return null;
                  return (
                    <label
                      title="Stitches below threshold: checked = fixed amount (1400), unchecked = normal rate calculation"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}
                    >
                      <input type="checkbox" checked={edit.apply_threshold} onChange={(e) => setEdit({ ...edit, apply_threshold: e.target.checked })} style={{ width: 14, height: 14 }} />
                      Apply threshold
                    </label>
                  );
                })()}
              </div>
              <div className="form-group">
                <label className="form-label">Amount (PKR)</label>
                <input
                  className="form-control"
                  readOnly
                  value={(() => {
                    const m = machines.find((x) => String(x.id) === String(edit.machine_id));
                    const amt = dayAmount(m, edit.stitch_count, edit.apply_threshold);
                    return amt === null || amt === undefined ? '-' : amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  })()}
                  style={{ backgroundColor: '#f5f5f5', fontWeight: 600, textAlign: 'right' }}
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'end', paddingBottom: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                  <input type="checkbox" checked={edit.special_thread} onChange={(e) => setEdit({ ...edit, special_thread: e.target.checked })} style={{ width: 16, height: 16 }} />
                  Special Thread
                </label>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Remarks</label>
              <input className="form-control" value={edit.remarks} onChange={(e) => setEdit({ ...edit, remarks: e.target.value })} placeholder="Remarks" />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}